import React, { useEffect, useState, useCallback } from 'react';
import {
  scoutApi, BiasState,
  ScoutInstrument, INSTRUMENTS, INSTRUMENT_DISPLAY,
} from '../../services/api';

type Direction = 'BUY' | 'SELL' | 'NONE';

const dirClass = (d: Direction): string => {
  switch (d) {
    case 'BUY':  return 'bias-dir-buy';
    case 'SELL': return 'bias-dir-sell';
    case 'NONE': return 'bias-dir-none';
  }
};

const hoursAgoLabel = (h: number | null): string => {
  if (h == null) return '—';
  if (h < 1) return `${Math.round(h * 60)}m ago`;
  return `${h.toFixed(1)}h ago`;
};

const DailyDirection: React.FC = () => {
  const [biases, setBiases] = useState<Record<ScoutInstrument, BiasState> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingInst, setPendingInst] = useState<ScoutInstrument | null>(null);
  const [noteEditing, setNoteEditing] = useState<ScoutInstrument | null>(null);
  const [noteDraft, setNoteDraft] = useState('');

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setBiases(await scoutApi.getBias());
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'fetch failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const setDirection = async (
    instrument: ScoutInstrument,
    direction: Direction,
    notes?: string,
  ) => {
    setPendingInst(instrument);
    setError(null);
    try {
      const result = await scoutApi.setBias({
        instrument, direction, set_by: 'frontend', notes,
      });
      if (result.cancelled_stale_triggers > 0) {
        // Surface this — the user should know we cleared stale triggers
        // alert isn't ideal but keeps the change minimal; can swap for a toast later
        console.info(
          `${instrument}: cancelled ${result.cancelled_stale_triggers} stale entry trigger(s) ` +
          `after direction flip from ${result.prior_direction} → ${direction}`,
        );
      }
      await fetch();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'set failed');
    } finally {
      setPendingInst(null);
      setNoteEditing(null);
      setNoteDraft('');
    }
  };

  return (
    <div className="daily-direction-section">
      <div className="daily-direction-header">
        <div>
          <h2 className="daily-direction-title">Daily Direction</h2>
          <p className="daily-direction-subtitle">
            Set the day's bias per instrument. The strategist only emits triggers matching the
            chosen direction; <strong>NONE</strong> pauses entries. Bias expires 24h after set.
            EXIT mode on open trades is unaffected.
          </p>
        </div>
        <button className="reviews-refresh-btn" onClick={fetch} disabled={loading}>
          {loading ? '...' : '↻ Refresh'}
        </button>
      </div>

      {error && <div className="advisor-error"><strong>Error:</strong> {error}</div>}

      <div className="bias-grid">
        {INSTRUMENTS.map(inst => {
          const b = biases?.[inst];
          const dir = (b?.direction || 'NONE') as Direction;
          const isPending = pendingInst === inst;
          const editingNotes = noteEditing === inst;
          return (
            <div key={inst} className={`bias-card ${dirClass(dir)}`}>
              <div className="bias-card-header">
                <span className="instrument-badge">{INSTRUMENT_DISPLAY[inst]}</span>
                <span className={`bias-current ${dirClass(dir)}`}>{dir}</span>
              </div>
              <div className="bias-card-meta">
                {b && b.set_at ? (
                  <>
                    <div>Set: {hoursAgoLabel(b.hours_ago)}</div>
                    {b.notes && <div className="bias-notes">"{b.notes}"</div>}
                  </>
                ) : (
                  <div className="bias-card-meta-dim">No bias set — entries paused</div>
                )}
              </div>
              <div className="bias-card-actions">
                <button
                  className={`bias-btn bias-btn-buy ${dir === 'BUY' ? 'active' : ''}`}
                  onClick={() => editingNotes ? setDirection(inst, 'BUY', noteDraft) : setDirection(inst, 'BUY')}
                  disabled={isPending}
                  title="Only BUY entries allowed"
                >
                  BUY
                </button>
                <button
                  className={`bias-btn bias-btn-sell ${dir === 'SELL' ? 'active' : ''}`}
                  onClick={() => editingNotes ? setDirection(inst, 'SELL', noteDraft) : setDirection(inst, 'SELL')}
                  disabled={isPending}
                  title="Only SELL entries allowed"
                >
                  SELL
                </button>
                <button
                  className={`bias-btn bias-btn-off ${dir === 'NONE' ? 'active' : ''}`}
                  onClick={() => setDirection(inst, 'NONE')}
                  disabled={isPending}
                  title="Pause new entries"
                >
                  OFF
                </button>
              </div>
              <div className="bias-card-notes-input">
                {editingNotes ? (
                  <input
                    type="text"
                    placeholder="Add note before flipping (optional)"
                    value={noteDraft}
                    onChange={e => setNoteDraft(e.target.value)}
                    onBlur={() => { setNoteEditing(null); setNoteDraft(''); }}
                    maxLength={200}
                    autoFocus
                  />
                ) : (
                  <button
                    type="button"
                    className="bias-note-toggle"
                    onClick={() => { setNoteEditing(inst); setNoteDraft(''); }}
                    disabled={isPending}
                  >
                    + add note
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DailyDirection;
